import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@shadcn-in-nx/ui/button";
import { TableCell, TableRow } from "@shadcn-in-nx/ui/table";
import { Badge } from "@shadcn-in-nx/ui/badge";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@shadcn-in-nx/ui/dropdown-menu";
import { Database, Workflow, MoreVertical, Copy, Trash2, Plus, ChevronRight, ChevronDown, AppWindowMac } from "lucide-react";

function DatabaseRow({ database, isExpanded, onToggle, onAddTable, onClone, onDelete }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const tableCount = database.tables?.length || 0;
    const workflows = database.connectedWorkflows || [];

    const handleNavigate = () => {
        const firstTable = database.tables?.[0];
        if (firstTable) {
            navigate(`/databases/${database.id}/tables/${firstTable.id}`, {
                state: { sidebarOpen: true }
            });
        }
    };

    return (
        <TableRow className="hover:bg-gray-50">
            <TableCell>
                <div className="flex items-center gap-2">
                    {tableCount > 0 ? (
                        <button onClick={onToggle} className="p-0.5 hover:bg-gray-200 rounded">
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                    ) : (
                        <span className="w-5" />
                    )}
                    <Database className="w-4 h-4 text-blue-600" />
                    <button onClick={handleNavigate} className="font-medium hover:text-blue-600 hover:underline text-left">
                        {database.name}
                    </button>
                    {tableCount > 0 && (
                        <Badge variant="outline" className="text-xs">
                            {tableCount} {tableCount === 1 ? 'table' : 'tables'}
                        </Badge>
                    )}
                </div>
            </TableCell>
            <TableCell className="text-muted-foreground text-sm hidden md:table-cell">{database.description || "—"}</TableCell>
            <TableCell className="hidden lg:table-cell">
                <WorkflowBadges workflows={workflows} />
            </TableCell>
            <TableCell>
                <div className="flex items-center justify-between">
                    <span className="text-sm">{new Date(database.updatedAt).toLocaleDateString()}</span>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => e.stopPropagation()}>
                                <MoreVertical className="w-4 h-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onAddTable(); }}>
                                <Plus className="w-4 h-4 mr-2" />
                                {t("databases.addTable")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onClone(); }}>
                                <Copy className="w-4 h-4 mr-2" />
                                {t("databases.cloneDatabase")}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-red-600 focus:text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />
                                {t("common.delete")}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </TableCell>
        </TableRow>
    );
}

function WorkflowBadges({ workflows }) {
    if (!workflows.length) return <span className="text-muted-foreground text-xs">—</span>;

    const maxVisible = 2;
    const visible = workflows.slice(0, maxVisible);
    const remaining = workflows.length - maxVisible;

    return (
        <div className="flex flex-wrap gap-1">
            {visible.map((workflow) => (
                <Badge key={workflow.id} variant="secondary" className="flex items-center gap-1">
                    {workflow.workflowType === "APP_V2" ? (
                        <AppWindowMac className="w-3 h-3" />
                    ) : (
                        <Workflow className="w-3 h-3" />
                    )}
                    {workflow.name}
                </Badge>
            ))}
            {remaining > 0 && (
                <Badge variant="outline" className="text-xs">+{remaining} more</Badge>
            )}
        </div>
    );
}

export default DatabaseRow;
